// Keep in sync with: src/lib/olmps/stage-ladder.ts (web) — this file must stay byte-identical.
//
// OLMPS STAGE Ladder — reads off POINTS, no engine of its own.
//
// Master Book rulings 1.5–1.8 (Update Directive 6 Oct 2026): there is ONE
// counting engine — points. The ladder used to count its own "4 step units"
// verdicts (unformed/forming/acquired/maintaining/lapsed) under a second rule
// set; that parallel engine is deleted. Every rung below is now just a view
// over the per-day points ledger (apps/mobile/src/olmps/gameSettings.ts):
//
//   STEP       today's Step actions (any completed action earns its mark)
//   TODAY      ordered when all five ORDER rows are green on the Today orb
//   AIM        a week is ordered when it holds `aimThreshold` ordered days
//              (over the user's week shape)
//   GOAL       a "month" of `goalWindowWeeks` weeks is ordered when it holds
//              `goalThreshold` ordered weeks
//   ENDEAVOUR  `endeavourThreshold` ordered months within `endeavourWindowMonths`
//
// Pure, deterministic, no DB/React/clock reads (caller passes `now`). The
// input types are structural, so gameSettings.ts's PointsLedger/GameSettings
// satisfy them without this file importing the app (it is mirrored to web).
// "RungState" labels are display words for where a rung stands — they are not
// verdicts and nothing is graded twice. There is no forgiveness: a short week
// is a short week (ruling 1.12).

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Rung = 'step' | 'today' | 'aim' | 'goal' | 'endeavour';
export type RungState = 'unformed' | 'forming' | 'acquired' | 'maintaining' | 'lapsed';

/** Legacy event shape — no longer an input (the ladder reads the ledger).
 *  Kept exported so older imports still compile. */
export interface CompletionEvent {
  rung: Rung;
  timestamp: string; // ISO 8601
  qualified: boolean;
}

/** Legacy per-rung config — thresholds now come from GameSettings. */
export interface RungConfig {
  acquireThreshold?: number;
  maintainThreshold?: number;
}
export interface LadderConfig {
  step?: RungConfig;
  today?: RungConfig;
  aim?: RungConfig;
  goal?: RungConfig;
  endeavour?: RungConfig;
}

/** One day of the points ledger (structural subset of gameSettings DayLedger). */
export interface LadderDay {
  stepActions?: number;
  /** Today-orb ORDER rows green, 0–5. */
  rowsGreen: number;
}
export type LadderLedger = Record<string, LadderDay>; // key = YYYY-MM-DD

/** The slice of GameSettings the ladder needs (structural). */
export interface LadderSettings {
  /** 0 = Sunday. The counted days of the week shape. */
  weekDays: number[];
  /** Saturday/Sunday count toward windows even when outside weekDays. */
  countWeekends: boolean;
  /** The weekly boundary day — the week opens the day after it. */
  weekBoundaryDay: number;
  aimThreshold: number;
  goalWindowWeeks: number;
  goalThreshold: number;
  endeavourWindowMonths: number;
  endeavourThreshold: number;
}

/** A time-boxed window. */
export interface Window {
  rung: Rung;
  startTime: string;
  endTime: string;
  events: CompletionEvent[];
  count: number;
}

/** Live pace projection for the current open window. */
export interface CurrentWindowProjection {
  count: number; // ordered units so far
  needed: number; // threshold to hit
  onTrack: boolean; // true if projected to meet threshold
  projected: number; // expected final count at window end
  elapsedFraction: number; // 0..1, fraction of window elapsed
}

/** State and projection for a single rung. */
export interface RungSnapshot {
  rung: Rung;
  state: RungState;
  /** History of recently closed windows (newest last). */
  history: Array<{
    startTime: string;
    endTime: string;
    count: number;
    state: RungState;
  }>;
  /** Projection for the current open window. */
  currentWindow: CurrentWindowProjection;
}

/** The complete ladder state. */
export interface LadderState {
  snapshot: string; // ISO 8601
  rungs: Record<Rung, RungSnapshot>;
}

// ---------------------------------------------------------------------------
// Calendar helpers (UTC day numbers from 'YYYY-MM-DD' — timezone-safe)
// ---------------------------------------------------------------------------

const MS_DAY = 86400000;
/** An ordered day = all five ORDER rows green (mirrors gameSettings.isOrderedDay). */
const ORDER_ROWS = 5;

function parseKey(key: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key);
  if (!m) return null;
  return Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / MS_DAY);
}
const weekdayOf = (dayNum: number) => (((dayNum + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday
/** Week ordinal — consecutive weeks differ by 1; weeks open the day after the boundary day. */
export function weekOrdinalOfDay(dayNum: number, boundaryDay: number): number {
  const open = (boundaryDay + 1) % 7;
  const back = (weekdayOf(dayNum) - open + 7) % 7;
  return (dayNum - back + 4 - open) / 7;
}
const weekStartDayNum = (ord: number, boundaryDay: number) => ord * 7 + ((boundaryDay + 1) % 7) - 4;

function isoDate(dayNum: number, end = false): string {
  const d = new Date(dayNum * MS_DAY);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${end ? '23:59:59' : '00:00:00'}`;
}

/** Is this weekday part of the counted week shape? */
function counted(dayNum: number, s: LadderSettings): boolean {
  const wd = weekdayOf(dayNum);
  return s.weekDays.includes(wd) || (s.countWeekends && (wd === 0 || wd === 6));
}

const stepsOf = (d: LadderDay | undefined) => Math.max(0, d?.stepActions ?? 0);
const isOrdered = (d: LadderDay | undefined) => !!d && d.rowsGreen >= ORDER_ROWS;

// ---------------------------------------------------------------------------
// Ordered weeks / months / seasons-of-months — shared with deity-progress
// ---------------------------------------------------------------------------

/** week ordinal → ordered days inside the counted week shape. */
export function orderedDaysByWeek(ledger: LadderLedger, s: LadderSettings): Map<number, number> {
  const out = new Map<number, number>();
  for (const [key, day] of Object.entries(ledger)) {
    const n = parseKey(key);
    if (n === null || !isOrdered(day) || !counted(n, s)) continue;
    const w = weekOrdinalOfDay(n, s.weekBoundaryDay);
    out.set(w, (out.get(w) ?? 0) + 1);
  }
  return out;
}

/** Ordered weeks (week ordinal list, ascending): ≥ aimThreshold ordered days. */
export function orderedWeeks(ledger: LadderLedger, s: LadderSettings): number[] {
  return [...orderedDaysByWeek(ledger, s)]
    .filter(([, n]) => n >= s.aimThreshold)
    .map(([w]) => w)
    .sort((a, b) => a - b);
}

/** "Month" = goalWindowWeeks aligned weeks. Returns month index → ordered weeks inside it. */
export function orderedWeeksByMonth(weeks: number[], s: LadderSettings): Map<number, number> {
  const len = Math.max(1, s.goalWindowWeeks);
  const out = new Map<number, number>();
  for (const w of weeks) {
    const m = Math.floor(w / len);
    out.set(m, (out.get(m) ?? 0) + 1);
  }
  return out;
}

/** Ordered months (index list, ascending): ≥ goalThreshold ordered weeks. */
export function orderedMonths(weeks: number[], s: LadderSettings): number[] {
  return [...orderedWeeksByMonth(weeks, s)]
    .filter(([, n]) => n >= s.goalThreshold)
    .map(([m]) => m)
    .sort((a, b) => a - b);
}

/** Weeks the Goal rung awards for: one per ordered month. (Month index → last ordered week in it.) */
export function goalAwardWeeks(weeks: number[], s: LadderSettings): number[] {
  const len = Math.max(1, s.goalWindowWeeks);
  const ordered = new Set(orderedMonths(weeks, s));
  const last = new Map<number, number>();
  for (const w of weeks) {
    const m = Math.floor(w / len);
    if (ordered.has(m)) last.set(m, Math.max(last.get(m) ?? -Infinity, w));
  }
  return [...last.values()].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Rung snapshots
// ---------------------------------------------------------------------------

interface SnapArgs {
  rung: Rung;
  counts: Map<number, number>; // window index → ordered-unit count
  needed: number;
  nowIdx: number;
  bounds: (idx: number) => { start: string; end: string };
  /** Units elapsed in the current window (incl. the current one) and its total, for pace. */
  elapsed: number;
  total: number;
  paced: boolean;
}

const wState = (count: number, needed: number): RungState => (count >= needed ? 'acquired' : count > 0 ? 'forming' : 'unformed');

function snap(a: SnapArgs): RungSnapshot {
  const cur = a.counts.get(a.nowIdx) ?? 0;
  const closedIdx = [...a.counts.keys()].filter((i) => i < a.nowIdx).sort((x, y) => x - y);
  const ordered = (i: number) => (a.counts.get(i) ?? 0) >= a.needed;
  const prevOrdered = ordered(a.nowIdx - 1);
  const everOrdered = closedIdx.some(ordered);

  let state: RungState;
  if (cur >= a.needed) state = prevOrdered ? 'maintaining' : 'acquired';
  else if (everOrdered) state = prevOrdered ? 'maintaining' : 'lapsed';
  else if (cur > 0 || closedIdx.length > 0) state = 'forming';
  else state = 'unformed';

  const history = closedIdx.slice(-10).map((i) => {
    const b = a.bounds(i);
    const n = a.counts.get(i) ?? 0;
    return { startTime: b.start, endTime: b.end, count: n, state: wState(n, a.needed) };
  });

  const projected = a.paced && a.elapsed > 0 ? Math.round((cur / a.elapsed) * a.total) : cur;
  const elapsedFraction = a.total > 0 ? Math.max(0, Math.min(1, a.elapsed / a.total)) : 0;
  return {
    rung: a.rung,
    state,
    history,
    currentWindow: {
      count: cur,
      needed: a.needed,
      onTrack: cur >= a.needed || (a.paced && projected >= a.needed),
      projected,
      elapsedFraction,
    },
  };
}

/**
 * The ladder, read off the points ledger. `now` is a local ISO string
 * ('YYYY-MM-DDTHH:mm:ss'); `_config` is legacy and ignored.
 */
export function computeLadder(
  ledger: LadderLedger,
  settings: LadderSettings,
  now: string,
  _config: LadderConfig = {},
): LadderState {
  const todayNum = parseKey(now) ?? 0;
  const b = settings.weekBoundaryDay;
  const nowWeek = weekOrdinalOfDay(todayNum, b);
  const monthLen = Math.max(1, settings.goalWindowWeeks);
  const nowMonth = Math.floor(nowWeek / monthLen);
  const endLen = Math.max(1, settings.endeavourWindowMonths);
  const nowEnd = Math.floor(nowMonth / endLen);

  // STEP — today's completed actions.
  const stepCounts = new Map<number, number>();
  // TODAY — rows green (ordered at 5).
  const todayCounts = new Map<number, number>();
  for (const [k, d] of Object.entries(ledger)) {
    const n = parseKey(k);
    if (n === null) continue;
    if (stepsOf(d) > 0) stepCounts.set(n, stepsOf(d));
    if (d.rowsGreen > 0) todayCounts.set(n, Math.min(ORDER_ROWS, d.rowsGreen));
  }
  const dayBounds = (i: number) => ({ start: isoDate(i), end: isoDate(i, true) });

  // AIM — ordered days per week.
  const aimCounts = orderedDaysByWeek(ledger, settings);
  const weekBounds = (w: number) => {
    const s = weekStartDayNum(w, b);
    return { start: isoDate(s), end: isoDate(s + 6, true) };
  };
  let countedElapsed = 0;
  let countedTotal = 0;
  const wsd = weekStartDayNum(nowWeek, b);
  for (let i = 0; i < 7; i++) {
    if (!counted(wsd + i, settings)) continue;
    countedTotal += 1;
    if (wsd + i <= todayNum) countedElapsed += 1;
  }

  // GOAL — ordered weeks per month; ENDEAVOUR — ordered months per window.
  const ow = orderedWeeks(ledger, settings);
  const goalCounts = orderedWeeksByMonth(ow, settings);
  const om = orderedMonths(ow, settings);
  const endCounts = new Map<number, number>();
  for (const m of om) {
    const e = Math.floor(m / endLen);
    endCounts.set(e, (endCounts.get(e) ?? 0) + 1);
  }
  const monthBounds = (m: number) => ({
    start: weekBounds(m * monthLen).start,
    end: weekBounds(m * monthLen + monthLen - 1).end,
  });
  const endBounds = (e: number) => ({
    start: weekBounds(e * endLen * monthLen).start,
    end: weekBounds((e + 1) * endLen * monthLen - 1).end,
  });

  return {
    snapshot: now,
    rungs: {
      step: snap({ rung: 'step', counts: stepCounts, needed: 1, nowIdx: todayNum, bounds: dayBounds, elapsed: 1, total: 1, paced: false }),
      today: snap({ rung: 'today', counts: todayCounts, needed: ORDER_ROWS, nowIdx: todayNum, bounds: dayBounds, elapsed: 1, total: 1, paced: false }),
      aim: snap({ rung: 'aim', counts: aimCounts, needed: settings.aimThreshold, nowIdx: nowWeek, bounds: weekBounds, elapsed: countedElapsed, total: countedTotal, paced: true }),
      goal: snap({
        rung: 'goal',
        counts: goalCounts,
        needed: settings.goalThreshold,
        nowIdx: nowMonth,
        bounds: monthBounds,
        elapsed: nowWeek - nowMonth * monthLen + 1,
        total: monthLen,
        paced: true,
      }),
      endeavour: snap({
        rung: 'endeavour',
        counts: endCounts,
        needed: settings.endeavourThreshold,
        nowIdx: nowEnd,
        bounds: endBounds,
        elapsed: nowMonth - nowEnd * endLen + 1,
        total: endLen,
        paced: true,
      }),
    },
  };
}
